var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { RequestStatus } from "../interfaces";
import buildPromiseResolver from "../util/promise-resolver";
import { composeExtendedPromise } from '../util/composition-util';
import { normalizeError } from "../util/error-record-util";
const TimeoutErrorCode = 'RemoteMethodTimeoutError'; // todo: to constants or custom error
export default class FlightReceipt {
    constructor(args) {
        this.status = RequestStatus.Pending;
        const { payload, updateRequestOpts, nodejsCallback, outboundStackTrace, responseErrorHandler, promiseSent } = args;
        Object.assign(this, { payload, updateRequestOpts, nodejsCallback });
        this._promiseSent = promiseSent;
        this._outboundStackTrace = outboundStackTrace;
        this.requestedAt = Date.now();
        this.responseResolver = buildPromiseResolver();
        this._responseErrorHandler = responseErrorHandler;
        if (this.rsvp === false) {
            this._markResolution(RequestStatus.SkipRsvp, null);
        }
    }
    isPending() {
        return this.status === RequestStatus.Pending;
    }
    info() {
        const { requestedAt, completedAt, status, payload } = this;
        return {
            requestId: payload.requestId,
            requestedAt: new Date(requestedAt),
            completedAt: completedAt ? new Date(completedAt) : void (0),
            status,
            requestPayload: payload
        };
    }
    infoForErrorRecord() {
        const _a = this.info(), { requestPayload } = _a, rest = __rest(_a, ["requestPayload"]);
        delete requestPayload.userArgs;
        return Object.assign(Object.assign(Object.assign({}, requestPayload), rest), { outboundRpcStack: this._outboundStackTrace });
    }
    forceResolve(...results) {
        this._markResolution(RequestStatus.ForcedResult, null, ...results);
        // todo: _nodejsCallback case
    }
    forceReject(reason) {
        this._markResolution(RequestStatus.ForcedError, reason);
        // todo: _nodejsCallback case
    }
    // todo: change rsvp / request opts. note: in next tick. Maybe RemoteRequest will pass in a callback for setting its own options, one that throws if already sent
    // opts(requestOpts: RequestOpts) {
    //
    // }
    updateTimeout(ms) {
        if (typeof this._timer === 'number') {
            clearTimeout(this._timer);
        }
        if (typeof ms === 'number' && ms > 0) {
            // @ts-ignore
            this._timer = setTimeout(() => this._markResolution(RequestStatus.TimeoutError, TimeoutErrorCode), ms);
        }
    }
    get rsvp() {
        return this.payload.rsvp;
    }
    requestOpts(opts) {
        this.updateRequestOpts(opts);
    }
    promiseSent() {
        return this._promiseSent;
    }
    _decoratedPromise() {
        const promise = this.responseResolver.promise;
        this._ensurePromiseDecorated(promise);
        // @ts-ignore
        return promise;
    }
    _remoteResponseReceived(error, ...result) {
        this._markResolution(error ? RequestStatus.RemoteError : RequestStatus.RemoteResult, error, ...result);
    }
    abortRequest(error) {
        this._markResolution(RequestStatus.RequestAborted, error);
    }
    _ensurePromiseDecorated(promise) {
        // @ts-ignore
        if (typeof promise.forceResolve === 'function') {
            return;
        }
        composeExtendedPromise(promise, this, FlightReceipt.prototype);
        // notes:
        // I first tried to make this class extend Promise. TypeScript made it awkward to stash the resolve/reject executor params
        // complaining about anything before super. Got past that with an extra superclass to do the stashing (alternatively it could
        // use plain js with prototype acrobatics) but the resulting behavior was broken. (eg, await would accept the promise but
        // would clobber any resulting value.) Lots posted on the subject but I decided to drop that line for now, just copying
        // the desired methods onto the promise.
        //
        // Alternatively, can maybe try `implements Promise`. ts declarations for them would be:
        // then!: <TResult1 = any, TResult2 = never>(onfulfilled?: ((value: any) => TResult1 | PromiseLike<TResult1>) | undefined | null, onrejected?: ((reason: any) => TResult2 | PromiseLike<TResult2>) | undefined | null) => Promise<TResult1 | TResult2>;
        // catch!: <TResult = never>(onrejected?: ((reason: any) => TResult | PromiseLike<TResult>) | undefined | null) => Promise<any | TResult>;
        // finally!: (onfinally?: (() => void) | undefined | null) => Promise<any>;
        // also: readonly [Symbol.toStringTag]: "Promise";
    }
    _markResolution(status, error, ...result) {
        if (!this.isPending()) {
            return; // warn/error/throw if resolved more than once? Maybe only if forced locally? (when you prob want to ignore the late-arriving remote response.)
        }
        this.completedAt = this.rsvp ? Date.now() : this.requestedAt;
        this.status = status;
        const _handleResponse = () => {
            const { resolve, reject } = this.responseResolver;
            if (error) {
                error = normalizeError(error);
                if (error.rpcInfo) {
                    error.rpcInfo.nested = error.rpcInfo.nested || [];
                    error.rpcInfo.nested.push(this.infoForErrorRecord());
                }
                else {
                    error.rpcInfo = this.infoForErrorRecord();
                }
                if (this._responseErrorHandler) {
                    try {
                        this._responseErrorHandler(error, resolve, reject);
                    }
                    catch (err) {
                        // todo: use logger & debugHandler
                        console.error('Uncaught error in responseErrorHandler', this.info(), err);
                        resolve({
                            error: {
                                errorCode: 'CustomResponseErrorHandlerFailed',
                                reason: 'callback provided to WranggleRpc an own error. If you want the response promise to throw, try using the reject function provided in the callback',
                                jsError: normalizeError(err),
                                rpcInfo: error.rpcInfo,
                            }
                        });
                    }
                }
                else {
                    resolve({ error });
                }
            }
            else {
                try {
                    resolve(...result);
                }
                catch (err) {
                    // todo: use debugHandler
                    console.error('Uncaught error in RemotePromise response handler', this.info(), err);
                }
            }
        };
        // needs to be in nextTick because sendRemoteRequest uses a nextTick and that should execute first.
        // todo: track and use timeout only when needed (when request opts change after remote request created)
        setTimeout(_handleResponse, 0);
    }
}
// aliases, just in case someone is actually using it (doubtful, very few downloads)
// @ts-ignore
FlightReceipt.prototype.resolveNow = FlightReceipt.prototype.forceResolve;
// @ts-ignore
FlightReceipt.prototype.rejectNow = FlightReceipt.prototype.rejectNow;
export { TimeoutErrorCode };
//# sourceMappingURL=flight-receipt.js.map